import { cn } from '@/lib/utils';

export interface SegmentedOption<T extends string | number> {
  label: string;
  value: T;
}

interface SegmentedControlProps<T extends string | number> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Theme-safe segmented toggle, pill-shaped like every S-FX control. The track
 * uses the neutral control surface (NOT the `secondary` brand color) so labels
 * stay readable in both themes; the active segment lifts to a card surface.
 */
export function SegmentedControl<T extends string | number>({
  options,
  value,
  onChange,
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={rest['aria-label']}
      className={cn(
        'inline-flex gap-0.5 rounded-full bg-tertiary/80 p-0.5 ring-1 ring-inset ring-border',
        className
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-full px-3 py-1.5 text-xs font-medium transition-[background-color,color,box-shadow] duration-fast ease-sfx',
              active
                ? 'bg-card text-foreground shadow-kumo-m ring-1 ring-inset ring-border'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
